import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LoadingDino } from '@/components/LoadingDino';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processGoogleCallback, isAuthenticated, checkAuth, isLoading } = useAuth();
  const hasProcessed = useRef(false);
  const isProcessing = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing...');
  const [sessionId, setSessionId] = useState(null);

  const processAuth = useCallback(async (sid) => {
    if (!sid) return;
    
    isProcessing.current = true;
    setError(null);
    setStatus('Verifying with server...');
    
    try {
      const data = await processGoogleCallback(sid);
      
      setStatus('Finalizing login...');
      await checkAuth();
      
      hasProcessed.current = true;
      setStatus('Success! Redirecting...');
      
      await new Promise(resolve => setTimeout(resolve, 300));
      navigate(data?.is_new_user ? '/account?welcome=1' : '/library', { replace: true });
    } catch (err) {
      console.error('Auth callback error:', err);
      setError(err.message || 'Authentication failed');
      isProcessing.current = false;
    }
  }, [processGoogleCallback, checkAuth, navigate]);

  useEffect(() => {
    if (hasProcessed.current || isProcessing.current) return;

    // Read session_id from URL fragment. Emergent OAuth always returns it
    // here; if it's missing we treat that as a hard failure and surface a
    // visible error rather than silently navigating away.
    const hash = location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sid = params.get('session_id');

    // Production diagnostic — visible in browser devtools even when we
    // can't reproduce locally. Helps narrow apex/www redirect issues.
    console.log('[AuthCallback]', {
      hasHash: Boolean(hash),
      sidPresent: Boolean(sid),
      origin: window.location.origin,
      pathname: window.location.pathname,
      isAuthenticated,
      isLoading,
    });

    if (!sid) {
      // Allow up to 2.5s for the hash to populate (some mobile browsers
      // parse `#fragment` slightly after first render). After that, show
      // the error explicitly — never silently redirect, otherwise the
      // user sees the homepage and assumes login broke for no reason.
      const timer = setTimeout(() => {
        if (!window.location.hash.includes('session_id')) {
          setError(
            'We didn\u2019t get a session from Google. This usually happens when ' +
            'a browser strips the URL fragment during the redirect (Opera, ' +
            'Samsung Internet) or a service worker served a cached page. ' +
            'Please try "Sign in again" \u2014 the new redirect URL should fix it.'
          );
        }
      }, 2500);
      return () => clearTimeout(timer);
    }

    setSessionId(sid);
    processAuth(sid);
  }, [location.hash, navigate, isAuthenticated, isLoading, processAuth]);

  const handleRetry = () => {
    // Google OAuth session_ids are single-use. Re-attempting with the same id
    // will always fail and surfaces a confusing error, so we send the user
    // back to /login to start the OAuth flow fresh.
    navigate('/login', { replace: true });
  };

  const handleBackToLogin = () => {
    navigate('/login', { replace: true });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-4" data-testid="auth-callback-error">
        <div className="text-red-500 text-lg text-center max-w-md">{error}</div>
        <div className="flex gap-3 mt-4">
          <Button onClick={handleRetry} variant="default" data-testid="auth-retry-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Sign in again
          </Button>
          <Button onClick={handleBackToLogin} variant="outline" data-testid="auth-back-login-btn">
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-callback-loading">
      <LoadingDino message={status} />
    </div>
  );
};

export default AuthCallback;
